/**
 * SettingsCompanyPage — Company logo, letterhead/footer, contact details
 *
 * Admin/manager only. Data stored in tenants.data JSONB column.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Building2, Camera, Save, Loader2, FileText, Palette, Bot, Copy, Check, MessageCircle, Globe } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { ApiRequestError, apiFetch } from '@/lib/api';
import { cn } from '@/lib/utils';
import {
  ASSISTANT_PERSONA_OPTIONS,
  GAMIFICATION_MODE_OPTIONS,
  PORTAL_THEME_OPTIONS,
  THEME_INTENSITY_OPTIONS,
  THEME_PALETTE_OPTIONS,
  VISUAL_PATTERN_OPTIONS,
  resolveBrandTheme,
  resolvePortalTheme,
  type AssistantPersonaId,
  type BrandThemeConfig,
  type GamificationMode,
  type PortalThemeTemplate,
  type ThemeIntensity,
  type ThemePaletteId,
  type VisualPatternId,
} from '@/lib/portal-theme';
import { useAuth } from '@/lib/auth';
import { ThemePatternBackground } from '@/components/ThemePatternBackground';
import { OpsChecklistCoach } from '@/components/checklist/OpsChecklistCoach';

interface CompanySettingsPayload {
  slug?: string;
  name?: string;
  logoUrl?: string;
  portalThemeTemplate?: PortalThemeTemplate;
  brandTheme?: BrandThemeConfig;
  letterHeaderHtml?: string;
  letterFooterHtml?: string;
  address?: string;
  phone?: string;
  email?: string;
  website?: string;
  registrationNo?: string;
  selfServicePortalEnabled?: boolean;
  aiEmailAccounts?: string[];
  aiWhatsappGroups?: Array<{ jid?: string; name: string }>;
}

async function fileToCompressedDataUrl(file: File): Promise<string> {
  const imageUrl = URL.createObjectURL(file);
  try {
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('Failed to read image'));
      img.src = imageUrl;
    });

    const maxDimension = 512;
    const scale = Math.min(1, maxDimension / Math.max(image.width, image.height));
    const width = Math.max(1, Math.round(image.width * scale));
    const height = Math.max(1, Math.round(image.height * scale));
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');
    if (!context) throw new Error('Failed to prepare image canvas');
    context.drawImage(image, 0, 0, width, height);

    const webp = canvas.toDataURL('image/webp', 0.86);
    if (webp.length <= 1_200_000) return webp;
    const jpeg = canvas.toDataURL('image/jpeg', 0.82);
    return jpeg;
  } finally {
    URL.revokeObjectURL(imageUrl);
  }
}

export default function SettingsCompanyPage() {
  const { t } = useTranslation();
  const logoRef = useRef<HTMLInputElement>(null);
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [companyName, setCompanyName] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [portalThemeTemplate, setPortalThemeTemplate] = useState<PortalThemeTemplate>('freight_forwarder');
  const [brandTheme, setBrandTheme] = useState<BrandThemeConfig>(() => resolveBrandTheme('freight_forwarder'));
  const [letterHeaderHtml, setLetterHeaderHtml] = useState('');
  const [letterFooterHtml, setLetterFooterHtml] = useState('');
  const [address, setAddress] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [website, setWebsite] = useState('');
  const [registrationNo, setRegistrationNo] = useState('');
  const [tenantSlug, setTenantSlug] = useState('');
  const [selfServicePortalEnabled, setSelfServicePortalEnabled] = useState(false);
  const [aiEmailAccounts, setAiEmailAccounts] = useState<string[]>([]);
  const [copiedEmail, setCopiedEmail] = useState('');
  const [emailStats, setEmailStats] = useState<Record<string, { total: number; processed: number; skipped: number }>>({});
  const [aiWhatsappGroups, setAiWhatsappGroups] = useState<Array<{ jid?: string; name: string }>>([]);
  const [copiedJid, setCopiedJid] = useState('');
  const [waGroups, setWaGroups] = useState<Array<{ id: string; group_jid: string; group_name: string; purpose?: string; created_at?: string }>>([]);
  const [showAddWa, setShowAddWa] = useState(false);
  const [newWaName, setNewWaName] = useState('');
  const [newWaParticipants, setNewWaParticipants] = useState('');
  const [waCreating, setWaCreating] = useState(false);
  const [waError, setWaError] = useState('');
  const [waDeletingJid, setWaDeletingJid] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      setError('Please sign in again to load company settings');
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError('');

    apiFetch<CompanySettingsPayload>('/auth/tenant/company')
      .then((d) => {
        if (cancelled) return;
        setCompanyName(d.name ?? '');
        setTenantSlug(d.slug ?? '');
        setLogoUrl(d.logoUrl ?? '');
        const nextTemplate = d.portalThemeTemplate ?? 'freight_forwarder';
        setPortalThemeTemplate(nextTemplate);
        setBrandTheme(resolveBrandTheme(nextTemplate, d.brandTheme));
        setLetterHeaderHtml(d.letterHeaderHtml ?? '');
        setLetterFooterHtml(d.letterFooterHtml ?? '');
        setAddress(d.address ?? '');
        setPhone(d.phone ?? '');
        setEmail(d.email ?? '');
        setWebsite(d.website ?? '');
        setRegistrationNo(d.registrationNo ?? '');
        setSelfServicePortalEnabled(d.selfServicePortalEnabled === true);
        setAiEmailAccounts(d.aiEmailAccounts ?? []);
        setAiWhatsappGroups(d.aiWhatsappGroups ?? []);
      })
      .catch((err) => {
        if (cancelled) return;
        if (err instanceof ApiRequestError && err.status === 401) {
          navigate('/login', { replace: true });
          return;
        }
        const message = err instanceof Error ? err.message : 'Failed to load company settings';
        setError(message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [authLoading, navigate, user]);

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSaved(false);
    try {
      await apiFetch('/auth/tenant/company', {
        method: 'PATCH',
        body: JSON.stringify({
          logoUrl,
          portalThemeTemplate,
          brandTheme: resolveBrandTheme(portalThemeTemplate, brandTheme),
          letterHeaderHtml,
          letterFooterHtml,
          address,
          phone,
          email,
          website,
          registrationNo,
        }),
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoFile = async (file: File) => {
    if (file.size > 2 * 1024 * 1024) {
      setError('Logo must be under 2 MB');
      return;
    }
    try {
      setError('');
      const compressed = await fileToCompressedDataUrl(file);
      setLogoUrl(compressed);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to process logo');
    }
  };

  const updateBrandTheme = (patch: BrandThemeConfig) => {
    setBrandTheme((current) => resolveBrandTheme(portalThemeTemplate, { ...current, ...patch }));
  };

  const loadEmailStats = useCallback(async () => {
    try {
      const stats = await apiFetch<Array<{ account: string; total: number; processed: number; skipped: number }>>('/email-messages/account-stats');
      const map: Record<string, { total: number; processed: number; skipped: number }> = {};
      for (const s of stats) map[s.account] = { total: s.total, processed: s.processed, skipped: s.skipped };
      setEmailStats(map);
    } catch {
      // non-critical
    }
  }, []);

  useEffect(() => {
    void loadEmailStats();
  }, [loadEmailStats]);

  const handleCopyEmail = useCallback((emailAddr: string) => {
    void navigator.clipboard.writeText(emailAddr).then(() => {
      setCopiedEmail(emailAddr);
      setTimeout(() => setCopiedEmail(''), 2000);
    });
  }, []);

  const loadWaGroups = useCallback(async () => {
    try {
      const res = await apiFetch<Array<{ id: string; group_jid: string; group_name: string; purpose?: string; created_at?: string }>>('/auth/tenant/whatsapp-groups');
      setWaGroups(res);
    } catch {
      // non-critical, keep empty
    }
  }, []);

  useEffect(() => {
    void loadWaGroups();
  }, [loadWaGroups]);

  const handleCreateWaGroup = useCallback(async () => {
    setWaError('');
    const name = newWaName.trim();
    const participants = newWaParticipants.split(',').map((p) => p.trim()).filter(Boolean);
    if (!name) { setWaError('Group name is required.'); return; }
    if (participants.length === 0) { setWaError('At least one participant phone number is required.'); return; }
    setWaCreating(true);
    try {
      await apiFetch('/auth/tenant/whatsapp-groups', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ groupName: name, participants }),
      });
      setNewWaName('');
      setNewWaParticipants('');
      setShowAddWa(false);
      await loadWaGroups();
    } catch (err) {
      setWaError(err instanceof ApiRequestError ? err.message : 'Failed to create group');
    } finally {
      setWaCreating(false);
    }
  }, [newWaName, newWaParticipants, loadWaGroups]);

  const handleDeleteWaGroup = useCallback(async (groupJid: string) => {
    setWaDeletingJid(groupJid);
    try {
      await apiFetch(`/auth/tenant/whatsapp-groups/${encodeURIComponent(groupJid)}`, { method: 'DELETE' });
      await loadWaGroups();
    } catch {
      // ignore
    } finally {
      setWaDeletingJid('');
    }
  }, [loadWaGroups]);

  const handleCopyJid = useCallback((jid: string) => {
    void navigator.clipboard.writeText(jid).then(() => {
      setCopiedJid(jid);
      setTimeout(() => setCopiedJid(''), 2000);
    });
  }, []);

  if (loading) {
    return (
      <div className="mx-auto max-w-2xl py-12 flex justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const selectedTheme = resolvePortalTheme(portalThemeTemplate);
  const selectedBrandTheme = resolveBrandTheme(portalThemeTemplate, brandTheme);
  const selectedPaletteOption = THEME_PALETTE_OPTIONS.find((option) => option.value === selectedBrandTheme.palette);
  const selectedPatternOption = VISUAL_PATTERN_OPTIONS.find((option) => option.value === selectedBrandTheme.visualPattern);
  const isAdmin = user?.role === 'admin' || user?.role === 'platform_admin';
  const canManageStorefront = user?.role === 'admin' || user?.role === 'manager' || user?.role === 'platform_admin';
  const storefrontHost = tenantSlug ? `https://${tenantSlug}.boox.network` : 'https://tenant.boox.network';
  const portalHost = tenantSlug ? `https://${tenantSlug}.boox.app` : 'https://tenant.boox.app';

  return (
    <div className="mx-auto max-w-2xl space-y-4 py-6 px-4">
      <div className="pb-1">
        <h1 className="text-xl font-bold">{t('settings.company', 'Company Settings')}</h1>
        <p className="mt-0.5 text-sm text-muted-foreground">
          {t('settings.companyDesc', 'Manage company logo, letterhead design, and contact info.')}
        </p>
      </div>

      {/* Company Logo */}
      <section className="rounded-xl border bg-card p-4">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
          <Building2 className="inline h-3.5 w-3.5 mr-1" />
          {t('settings.companyLogo', 'Company Logo')}
        </h2>
        <div className="flex items-center gap-4">
          <button onClick={() => logoRef.current?.click()} className="relative group">
            {logoUrl ? (
              <img src={logoUrl} alt="Logo" className="h-20 w-auto max-w-[200px] rounded-lg border object-contain bg-white p-1" />
            ) : (
              <div className="flex h-20 w-32 items-center justify-center rounded-lg border-2 border-dashed border-muted-foreground/30 text-muted-foreground">
                <Camera className="h-6 w-6" />
              </div>
            )}
            <div className="absolute inset-0 flex items-center justify-center rounded-lg bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity">
              <Camera className="h-6 w-6 text-white" />
            </div>
          </button>
          <div className="text-sm text-muted-foreground">
            <p>{t('settings.logoHint', 'Upload your company logo. Max 2 MB. Used in reports and documents.')}</p>
            {logoUrl && (
              <button onClick={() => setLogoUrl('')} className="text-destructive hover:underline text-xs mt-1">
                {t('common.remove', 'Remove')}
              </button>
            )}
          </div>
          <input
            ref={logoRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void handleLogoFile(f);
              e.target.value = '';
            }}
          />
        </div>
      </section>

      {/* Company Info */}
      <section className="rounded-xl border bg-card p-4 space-y-3">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          {t('settings.companyInfo', 'Company Information')}
        </h2>

        <div>
          <label className="block text-sm font-medium mb-1">{t('settings.companyName', 'Company Name')}</label>
          <input
            type="text"
            value={companyName}
            disabled
            className="w-full rounded-lg border border-input bg-muted px-3 py-2 text-sm text-muted-foreground cursor-not-allowed"
          />
          <p className="text-xs text-muted-foreground mt-0.5">{t('settings.companyNameReadonly', 'Contact support to change.')}</p>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium mb-1">{t('settings.phone', 'Phone')}</label>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
              placeholder="+65 6123 4567"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">{t('settings.email', 'Email')}</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
              placeholder="info@company.com"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium mb-1">{t('settings.website', 'Website')}</label>
            <input
              type="url"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
              placeholder="https://company.com"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">{t('settings.registrationNo', 'Registration No.')}</label>
            <input
              type="text"
              value={registrationNo}
              onChange={(e) => setRegistrationNo(e.target.value)}
              className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
              placeholder="202012345A"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">{t('settings.address', 'Address')}</label>
          <textarea
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            rows={2}
            className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none"
            placeholder="123 Business Park Dr, #01-01, Singapore 123456"
          />
        </div>
      </section>

      <section className="rounded-xl border bg-card p-4 space-y-3">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          <Palette className="inline h-3.5 w-3.5 mr-1" />
          {t('settings.portalTheme', 'Portal Theme Template')}
        </h2>
        <p className="text-xs text-muted-foreground">
          {t('settings.portalThemeDesc', 'Choose the design system used by your self-service portal, tenant login page, and customer workspace shell.')}
        </p>

        <div className="grid gap-3 md:grid-cols-2">
          {PORTAL_THEME_OPTIONS.map((option) => {
            const isSelected = option.value === portalThemeTemplate;
            return (
              <button
                key={option.value}
                type="button"
                onClick={() => {
                  setPortalThemeTemplate(option.value);
                  setBrandTheme(option.defaultBrandTheme);
                }}
                className={cn(
                  'rounded-xl border p-3 text-left transition-all',
                  isSelected
                    ? 'border-primary bg-primary/5 shadow-sm'
                    : 'border-border hover:border-primary/40 hover:bg-muted/40',
                )}
              >
                <div className={cn('h-16 rounded-lg bg-gradient-to-br', option.preview)} />
                <div className="mt-3 flex items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-foreground">{option.label}</p>
                    <p className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">{option.sector}</p>
                  </div>
                  {isSelected && (
                    <span className="rounded-full bg-primary px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-primary-foreground">
                      Active
                    </span>
                  )}
                </div>
                <p className="mt-2 text-xs text-muted-foreground">{option.description}</p>
              </button>
            );
          })}
        </div>

        <div className="rounded-xl border border-border bg-muted/20 p-3">
          <div className="mb-3">
            <p className="text-sm font-semibold text-foreground">Color + Background Pattern</p>
            <p className="mt-0.5 text-xs text-muted-foreground">
              Choose these together: color controls the brand tone, pattern controls the logistics graphic used in login, portal, and preview surfaces.
            </p>
          </div>

          <div className="grid gap-3 md:grid-cols-2">
            <label className="space-y-1.5">
              <span className="text-sm font-medium">Theme Color</span>
              <div className="flex gap-2">
                <span className={cn('h-10 w-12 shrink-0 rounded-lg bg-gradient-to-br', selectedPaletteOption?.preview ?? 'from-blue-900 to-cyan-400')} />
                <select
                  value={selectedBrandTheme.palette}
                  onChange={(e) => updateBrandTheme({ palette: e.target.value as ThemePaletteId })}
                  className="h-10 min-w-0 flex-1 rounded-lg border border-input bg-background px-3 text-sm"
                >
                  {THEME_PALETTE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
              <span className="block text-xs text-muted-foreground">
                {selectedPaletteOption?.description ?? 'Controls the main brand color tokens.'}
              </span>
            </label>

            <label className="space-y-1.5">
              <span className="text-sm font-medium">Background Pattern</span>
              <select
                value={selectedBrandTheme.visualPattern}
                onChange={(e) => updateBrandTheme({ visualPattern: e.target.value as VisualPatternId })}
                className="h-10 w-full rounded-lg border border-input bg-background px-3 text-sm"
              >
                {VISUAL_PATTERN_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
              <span className="block text-xs text-muted-foreground">
                {selectedPatternOption?.description ?? 'Controls the vector graphic motif.'}
              </span>
            </label>
          </div>
        </div>

        <div className="grid gap-3 md:grid-cols-2">
          <label className="space-y-1.5">
            <span className="text-sm font-medium">Assistant Persona</span>
            <select
              value={selectedBrandTheme.assistantPersona}
              onChange={(e) => updateBrandTheme({ assistantPersona: e.target.value as AssistantPersonaId })}
              className="h-10 w-full rounded-lg border border-input bg-background px-3 text-sm"
            >
              {ASSISTANT_PERSONA_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </label>

          <label className="space-y-1.5">
            <span className="text-sm font-medium">Gamification</span>
            <select
              value={selectedBrandTheme.gamificationMode}
              onChange={(e) => updateBrandTheme({ gamificationMode: e.target.value as GamificationMode })}
              className="h-10 w-full rounded-lg border border-input bg-background px-3 text-sm"
            >
              {GAMIFICATION_MODE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </label>
        </div>

        <div className="grid gap-3 md:grid-cols-2">
          <label className="space-y-1.5">
            <span className="text-sm font-medium">Background Intensity</span>
            <select
              value={selectedBrandTheme.intensity}
              onChange={(e) => updateBrandTheme({ intensity: e.target.value as ThemeIntensity })}
              className="h-10 w-full rounded-lg border border-input bg-background px-3 text-sm"
            >
              {THEME_INTENSITY_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </label>

          <div className="rounded-lg border border-border bg-muted/30 px-3 py-2">
            <p className="text-sm font-medium">Production guidance</p>
            <p className="mt-1 text-xs text-muted-foreground">
              Sidebar uses a low-noise grid by default. Playful persona modes should stay in training or personal workspace unless approved.
            </p>
          </div>
        </div>

        <div className="overflow-hidden rounded-xl border border-border/60 bg-background">
          <div className="relative min-h-[160px] overflow-hidden p-4 text-white" style={selectedTheme.shellStyle}>
            <div className="absolute inset-0" style={selectedTheme.textureStyle} />
            <ThemePatternBackground
              pattern={selectedBrandTheme.visualPattern}
              palette={selectedBrandTheme.palette}
              intensity={selectedBrandTheme.intensity}
              tone="onDark"
              className="pointer-events-none absolute inset-0 h-full w-full"
            />
            <div className="relative space-y-2">
              <p className="text-[11px] uppercase tracking-[0.16em] text-white/70">{selectedTheme.sector}</p>
              <p className="text-lg font-semibold">{companyName || 'Your Company Portal'}</p>
              <p className="text-sm text-white/75">{selectedTheme.description}</p>
              <div className="flex flex-wrap gap-2 pt-2">
                <span className="rounded-full bg-white/15 px-2.5 py-1 text-[11px] font-semibold">{selectedBrandTheme.palette}</span>
                <span className="rounded-full bg-white/15 px-2.5 py-1 text-[11px] font-semibold">{selectedBrandTheme.visualPattern}</span>
                <span className="rounded-full bg-white/15 px-2.5 py-1 text-[11px] font-semibold">{selectedBrandTheme.assistantPersona}</span>
                <span className="rounded-full bg-white/15 px-2.5 py-1 text-[11px] font-semibold">{selectedBrandTheme.gamificationMode}</span>
              </div>
            </div>
          </div>
          <div className="flex items-center justify-between gap-3 px-4 py-3 bg-background/90">
            <div>
              <p className="text-sm font-medium text-foreground">Preview scope</p>
              <p className="text-xs text-muted-foreground">Tenant login, public frontpage, portal shell</p>
            </div>
            <span className="rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.12em]" style={selectedTheme.cardStyle}>
              {selectedTheme.label}
            </span>
          </div>
          <div className="border-t border-border/60 bg-background px-4 py-3">
            <p className="text-sm font-medium text-foreground">Workflow checklist preview</p>
            <p className="mt-0.5 text-xs text-muted-foreground">
              Shows how the assistant persona nudges users through operational steps.
            </p>
            {selectedBrandTheme.gamificationMode === 'off' ? (
              <div className="mt-3 rounded-lg border border-dashed border-border px-3 py-2 text-xs text-muted-foreground">
                Gamification is off. Production checklists stay as plain operational controls.
              </div>
            ) : (
              <OpsChecklistCoach
                assistantPersona={selectedBrandTheme.assistantPersona}
                gamificationMode={selectedBrandTheme.gamificationMode}
                palette={selectedBrandTheme.palette}
                completed={3}
                total={6}
                percent={50}
                nextStepLabel="Confirm carrier release and delivery order"
                contextLabel="Shipment runbook"
              />
            )}
          </div>
        </div>
      </section>

      {selfServicePortalEnabled && canManageStorefront && (
        <section className="rounded-xl border bg-card p-4 space-y-3">
          <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            <Globe className="inline h-3.5 w-3.5 mr-1" />
            Storefront Lite
          </h2>
          <p className="text-sm text-muted-foreground">
            Configure the standard `boox.network` frontpage and online service modules. This is for online services, not free-form website design.
          </p>
          <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
            <span className="rounded-full border px-2.5 py-1">{storefrontHost}</span>
            <span className="rounded-full border px-2.5 py-1">{portalHost}</span>
          </div>
          <div className="flex items-center justify-between gap-3 rounded-xl border border-primary/20 bg-primary/5 px-4 py-3">
            <div>
              <p className="text-sm font-medium text-foreground">Standard pages + module toggles</p>
              <p className="text-xs text-muted-foreground">Manage hero copy, services/contact pages, tracking, sailing schedule, booking, inquiry, login, and AI chat.</p>
            </div>
            <Link
              to="/settings/storefront"
              className="inline-flex shrink-0 items-center rounded-lg bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
            >
              Open Storefront
            </Link>
          </div>
        </section>
      )}

      {/* Letterhead / Footer */}
      <section className="rounded-xl border bg-card p-4 space-y-3">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          <FileText className="inline h-3.5 w-3.5 mr-1" />
          {t('settings.letterDesign', 'Letter / Document Design')}
        </h2>
        <p className="text-xs text-muted-foreground">
          {t('settings.letterDesignDesc', 'HTML templates for document headers and footers. Used when printing invoices, delivery orders, and reports.')}
        </p>

        <div>
          <label className="block text-sm font-medium mb-1">{t('settings.letterHeader', 'Header Template (HTML)')}</label>
          <textarea
            value={letterHeaderHtml}
            onChange={(e) => setLetterHeaderHtml(e.target.value)}
            rows={4}
            className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary/30 resize-y"
            placeholder='<div style="text-align:center"><img src="{{logoUrl}}" height="40" /> <h2>{{companyName}}</h2></div>'
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">{t('settings.letterFooter', 'Footer Template (HTML)')}</label>
          <textarea
            value={letterFooterHtml}
            onChange={(e) => setLetterFooterHtml(e.target.value)}
            rows={3}
            className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary/30 resize-y"
            placeholder='<div style="text-align:center;font-size:10px">{{companyName}} | {{address}} | {{phone}}</div>'
          />
        </div>

        {/* Live preview */}
        {(letterHeaderHtml || letterFooterHtml) && (
          <div className="border rounded-lg p-3 bg-white dark:bg-slate-900">
            <p className="text-[10px] uppercase text-muted-foreground mb-1">{t('settings.preview', 'Preview')}</p>
            {letterHeaderHtml && (
              <div className="border-b pb-2 mb-2" dangerouslySetInnerHTML={{ __html: letterHeaderHtml }} />
            )}
            <p className="text-xs text-muted-foreground italic text-center py-4">[{t('settings.documentBody', 'Document body content')}]</p>
            {letterFooterHtml && (
              <div className="border-t pt-2 mt-2" dangerouslySetInnerHTML={{ __html: letterFooterHtml }} />
            )}
          </div>
        )}
      </section>

      {/* AI Agent Channels — Admin only */}
      {isAdmin && aiEmailAccounts.length > 0 && (
        <section className="rounded-xl border bg-card p-4 space-y-3">
          <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            <Bot className="inline h-3.5 w-3.5 mr-1" />
            {t('settings.aiAgent', 'AI Agent Email')}
          </h2>
          <p className="text-xs text-muted-foreground">
            {t('settings.aiAgentDesc', 'Forward haulier instructions, DOs, and booking requests to this address. The AI agent will automatically create jobs for you.')}
          </p>
          <div className="space-y-2">
            {aiEmailAccounts.map((addr) => {
              const stat = emailStats[addr];
              return (
                <div key={addr} className="rounded-lg border bg-muted/40 px-3 py-2 space-y-2">
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => navigate(`/inbox/email-messages?toAddr=${encodeURIComponent(addr)}`)}
                      className="flex-1 text-sm font-mono text-left text-sky-700 dark:text-sky-400 hover:underline truncate"
                      title={t('settings.viewEmailMsgs', 'View messages for this account')}
                    >
                      {addr}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleCopyEmail(addr)}
                      className="flex items-center gap-1 rounded-md px-2 py-1 text-xs text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                      title={t('common.copy', 'Copy')}
                    >
                      {copiedEmail === addr
                        ? <><Check className="h-3.5 w-3.5 text-green-500" />{t('common.copied', 'Copied!')}</>
                        : <><Copy className="h-3.5 w-3.5" />{t('common.copy', 'Copy')}</>}
                    </button>
                  </div>
                  {stat && (
                    <div className="flex items-center gap-3 text-xs text-muted-foreground">
                      <span className="font-medium text-foreground">{stat.total}</span> total
                      <span className="text-green-600 font-medium">{stat.processed}</span> processed
                      <span className="text-amber-600 font-medium">{stat.skipped}</span> skipped
                    </div>
                  )}
                  {!stat && (
                    <p className="text-xs text-muted-foreground italic">{t('settings.noEmailYet', 'No messages yet')}</p>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      )}

      {isAdmin && (
        <section className="rounded-xl border bg-card p-4 space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              <MessageCircle className="inline h-3.5 w-3.5 mr-1" />
              {t('settings.aiWhatsapp', 'AI Agent WhatsApp Groups')}
            </h2>
            <button
              type="button"
              onClick={() => { setShowAddWa((v) => !v); setWaError(''); }}
              className="flex items-center gap-1 rounded-md px-2 py-1 text-xs bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
            >
              {showAddWa ? t('common.cancel', 'Cancel') : t('settings.addWaGroup', '+ Add Group')}
            </button>
          </div>
          <p className="text-xs text-muted-foreground">
            {t('settings.aiWhatsappDesc', 'WhatsApp groups monitored and replied to by the AI agent.')}
          </p>

          {/* Existing groups */}
          {waGroups.length > 0 && (
            <div className="space-y-2">
              {waGroups.map((grp) => (
                <div key={grp.group_jid} className="rounded-lg border bg-muted/40 px-3 py-2 space-y-1">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-medium text-foreground">{grp.group_name}</span>
                    <button
                      type="button"
                      onClick={() => void handleDeleteWaGroup(grp.group_jid)}
                      disabled={waDeletingJid === grp.group_jid}
                      className="flex items-center gap-1 rounded-md px-2 py-1 text-xs text-destructive hover:bg-destructive/10 transition-colors disabled:opacity-50"
                    >
                      {waDeletingJid === grp.group_jid
                        ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        : t('common.remove', 'Remove')}
                    </button>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="flex-1 text-xs font-mono text-muted-foreground truncate">{grp.group_jid}</span>
                    <button
                      type="button"
                      onClick={() => handleCopyJid(grp.group_jid)}
                      className="flex items-center gap-1 rounded-md px-2 py-1 text-xs text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                      title={t('common.copy', 'Copy')}
                    >
                      {copiedJid === grp.group_jid
                        ? <><Check className="h-3.5 w-3.5 text-green-500" />{t('common.copied', 'Copied!')}</>
                        : <><Copy className="h-3.5 w-3.5" />{t('common.copy', 'Copy')}</>}
                    </button>
                  </div>
                  {grp.purpose && (
                    <span className="text-xs text-muted-foreground">{grp.purpose}</span>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Also show groups from brain config that aren't in the DB yet */}
          {aiWhatsappGroups.filter((g) => g.jid && !waGroups.some((w) => w.group_jid === g.jid)).map((grp) => (
            <div key={grp.name} className="rounded-lg border bg-muted/20 px-3 py-2 space-y-1">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-foreground">{grp.name}</span>
                <span className="text-xs text-muted-foreground bg-muted rounded px-1">legacy</span>
              </div>
              {grp.jid && (
                <div className="flex items-center gap-2">
                  <span className="flex-1 text-xs font-mono text-muted-foreground truncate">{grp.jid}</span>
                  <button
                    type="button"
                    onClick={() => handleCopyJid(grp.jid!)}
                    className="flex items-center gap-1 rounded-md px-2 py-1 text-xs text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                    title={t('common.copy', 'Copy')}
                  >
                    {copiedJid === grp.jid
                      ? <><Check className="h-3.5 w-3.5 text-green-500" />{t('common.copied', 'Copied!')}</>
                      : <><Copy className="h-3.5 w-3.5" />{t('common.copy', 'Copy')}</>}
                  </button>
                </div>
              )}
            </div>
          ))}

          {/* Add group form */}
          {showAddWa && (
            <div className="rounded-lg border border-dashed bg-muted/20 p-3 space-y-3">
              <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground">
                  {t('settings.waGroupName', 'Group Name')}
                </label>
                <input
                  type="text"
                  value={newWaName}
                  onChange={(e) => setNewWaName(e.target.value)}
                  placeholder={t('settings.waGroupNamePh', 'e.g. Company x Boox Ops')}
                  className="w-full rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground">
                  {t('settings.waParticipants', 'Participants (phone numbers, comma-separated)')}
                </label>
                <input
                  type="text"
                  value={newWaParticipants}
                  onChange={(e) => setNewWaParticipants(e.target.value)}
                  placeholder={t('settings.waParticipantsPh', 'e.g. 6512345678, 6598322006')}
                  className="w-full rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
                />
                <p className="text-xs text-muted-foreground">
                  {t('settings.waParticipantsHint', 'Include country code without +. The AI bot will be added as admin.')}
                </p>
              </div>
              {waError && (
                <p className="text-xs text-destructive bg-destructive/10 px-2 py-1 rounded">{waError}</p>
              )}
              <button
                type="button"
                onClick={() => void handleCreateWaGroup()}
                disabled={waCreating}
                className="flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50"
              >
                {waCreating && <Loader2 className="h-4 w-4 animate-spin" />}
                {t('settings.createWaGroup', 'Create Group')}
              </button>
            </div>
          )}
        </section>
      )}

      {/* Save */}
      {error && (
        <p className="text-sm text-destructive bg-destructive/10 px-3 py-2 rounded-lg">{error}</p>
      )}
      {saved && (
        <p className="text-sm text-green-600 bg-green-50 dark:bg-green-900/20 px-3 py-2 rounded-lg">
          {t('settings.companySaved', 'Company settings saved successfully.')}
        </p>
      )}
      <button
        onClick={handleSave}
        disabled={saving}
        className={cn(
          'flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors',
          'bg-primary text-primary-foreground hover:bg-primary/90',
          saving && 'opacity-50 cursor-wait',
        )}
      >
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
        {t('common.save', 'Save')}
      </button>
    </div>
  );
}
